// src/features/workspace/context.ts
// The workspace state every control on a list page reads and writes.
//
// It lives here rather than in WorkspaceLayout because the controls no longer
// render inside the layout: the view switcher, the filters and the create
// button paint in the pane's tab strip through a portal. A portal keeps React
// context, so they still read this — but the module graph would be a cycle if
// the context were defined in the layout that imports them.

import { createContext, useContext } from 'react';

import type {
  DensityMode,
  FilterValue,
  ViewType,
  WorkspaceConfig,
  WorkspaceState,
} from './types';

export type WorkspaceAction =
  | { type: 'SET_VIEW'; view: ViewType }
  | { type: 'SET_GROUP_BY'; groupBy: string | null }
  | { type: 'SET_SORT_BY'; field: string; direction?: 'asc' | 'desc' }
  | { type: 'SET_DENSITY'; density: DensityMode }
  | { type: 'SET_SEARCH'; search: string }
  | { type: 'ADD_FILTER'; filter: FilterValue }
  | { type: 'REMOVE_FILTER'; field: string }
  | { type: 'CLEAR_FILTERS' }
  /**
   * Replace the whole state at once. Applying a saved view moves the view
   * type, every filter, the search, the sort and the grouping together; doing
   * that as a sequence of single-field actions would write the URL — and fire
   * a request — once per field.
   */
  | { type: 'SET_STATE'; state: WorkspaceState };

export interface WorkspaceContextValue {
  state: WorkspaceState;
  /** The state an untouched workspace starts from, derived from `config`. */
  defaults: WorkspaceState;
  config: WorkspaceConfig;
  dispatch: (action: WorkspaceAction) => void;
  /** Live search text, ahead of the debounce that commits it to the URL. */
  searchDraft: string;
  onSearchDraftChange: (search: string) => void;
}

export const WorkspaceCtx = createContext<WorkspaceContextValue | null>(null);

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceCtx);
  if (!ctx) {
    throw new Error('useWorkspace must be used within a <WorkspaceLayout>');
  }
  return ctx;
}
